/**
 * TextElement - Editor element for text fields with enable/disable checkbox
 *
 * Features:
 * - Checkbox for enable/disable state
 * - Text input field
 * - Gear icon for style settings (when styleFields provided)
 * - Style settings panel (expandable)
 *
 * Sprint: screen-editor-header
 * Task: Phase 3 - TextElement
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Box, Checkbox, IconButton, Typography, Collapse, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { StyledTextField, WithGuideHelp } from '@modules/ui-components';
import { PaywallVariableTextField } from '../../components/shared/PaywallVariableTextField';
import type { FieldConfig } from '../../types/editorFieldTypes';
import { useEditorGuideService } from '../../contexts';
import {
  mainFieldInputSx,
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  SHOW_FIELD_PATH_TOOLTIP,
  expandedSectionSx,
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../config/editorFieldStyles';
import {
  FIELD_MARGIN_BOTTOM,
  FIELD_GAP,
  FIELD_GAP_SMALL,
  FIELD_STYLE_GRID_GAP,
  FIELD_STYLE_MARGIN_TOP,
  FIELD_STYLE_PADDING_LEFT,
} from '../../config/ui';
import { StyleFieldRow, ButtonStyleSections } from '../../components/shared/styleFields';
import { PaddingElement } from './PaddingElement';
import type { PaddingValues } from './PaddingElement';
import { getStyleInputTestId, getStyleCheckboxTestId } from '../../utils/testIdUtils';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';

/**
 * Props for TextElement component
 */
export interface TextElementProps {
  /** Field configuration */
  fieldConfig: FieldConfig;

  /** Current value */
  value: string;

  /** Template value for restoration */
  templateValue: unknown;

  /** Whether field is enabled (checkbox state) */
  enabled: boolean;

  /** Current locale for l10n */
  locale: string;

  /** Callback when value changes */
  onChange: (value: string) => void;

  /** Callback when enabled state changes */
  onEnabledChange: (enabled: boolean) => void;

  /** Whether entire element is disabled (save state) */
  disabled?: boolean;

  /** Whether style settings are expanded */
  expandedStyles?: boolean;

  /** Callback when style expansion toggles */
  onStylesExpandToggle?: () => void;

  /** Style values for nested style fields (text content styles) */
  styleValues?: Record<string, unknown>;

  /** Button container style values (ButtonBlock styles) */
  buttonStyleValues?: Record<string, unknown>;

  /** Callback when style value changes */
  onStyleChange?: (path: string, value: unknown) => void;

  /** URI value for NavLink fields */
  uriValue?: string;

  /** Callback when URI value changes */
  onUriChange?: (value: string) => void;

  /** data-testid for the main field container */
  'data-testid'?: string;

  /** data-testid for the checkbox */
  checkboxTestId?: string;

  /** data-testid for the styles toggle button (gear icon) */
  stylesToggleTestId?: string;

  /** data-testid for the input field */
  inputTestId?: string;

  /** data-testid for the expanded text-styles grid (clips the whole style group) */
  styleGroupTestId?: string;

  /** Enable paywall variable insertion (@ trigger) for this field */
  enablePaywallVariables?: boolean;

  /** Project ID for icon upload (V2 only) */
  projectId?: string;

  /** Onboarding ID for icon upload (V2 only) */
  onboardingId?: string;

  /** Version ID for icon upload (V2 only) */
  versionId?: string;
}

/**
 * TextElement - Editor element for text fields
 *
 * Layout:
 * [✓] Label: [Text Input_______________] [⚙]
 *
 * When gear is expanded:
 * [✓] Label: [Text Input_______________] [⚙]
 * ├── [✓] Text Color: [ColorPicker]
 * └── [✓] Font Size: [NumberInput]
 */
export const TextElement: React.FC<TextElementProps> = ({
  fieldConfig,
  value,
  templateValue,
  enabled,
  locale,
  onChange,
  onEnabledChange,
  disabled = false,
  expandedStyles: expandedStylesProp,
  onStylesExpandToggle,
  styleValues = {},
  buttonStyleValues = {},
  onStyleChange,
  'data-testid': dataTestId,
  checkboxTestId,
  stylesToggleTestId,
  inputTestId,
  styleGroupTestId,
  enablePaywallVariables = false,
  uriValue,
  onUriChange,
  projectId,
  onboardingId,
  versionId,
}) => {
  // Internal state for style expansion (controlled externally or internally)
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isStylesExpanded = expandedStylesProp ?? internalExpanded;

  const hasStyleFields = fieldConfig.styleFields && fieldConfig.styleFields.length > 0;
  const hasButtonStyleFields = fieldConfig.buttonStyleFields && fieldConfig.buttonStyleFields.length > 0;
  const hasButtonStyleGroups = fieldConfig.buttonStyleGroups && fieldConfig.buttonStyleGroups.length > 0;
  const hasPaddingConfig = !!fieldConfig.paddingConfig;
  const hasUriField = !!fieldConfig.uriPath;
  const hasAnyStyleFields = hasStyleFields || hasButtonStyleFields || hasButtonStyleGroups || hasPaddingConfig || hasUriField;

  // Upload service for icon (V2 only, created lazily)
  const uploadService = useMemo(() => {
    if (hasButtonStyleGroups && projectId && onboardingId && versionId) {
      return new OnboardingMediaUploadService();
    }
    return null;
  }, [hasButtonStyleGroups, projectId, onboardingId, versionId]);

  const handleIconUpload = useCallback(
    async (file: File): Promise<string> => {
      if (!uploadService) throw new Error('Upload service not available');
      const result = await uploadService.uploadImage(file, {
        projectId: projectId!,
        onboardingId: onboardingId!,
        versionId: versionId!,
        locale,
      });
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale],
  );

  // Get guideService from context for WithGuideHelp
  const guideService = useEditorGuideService();

  // Handle checkbox toggle
  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      onEnabledChange(event.target.checked);
    },
    [disabled, onEnabledChange]
  );

  // Handle text input change
  const handleInputChange = useCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    [onChange]
  );

  // Handle gear icon click
  const handleGearClick = useCallback(() => {
    if (onStylesExpandToggle) {
      onStylesExpandToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  }, [onStylesExpandToggle]);

  // Handle style field value change
  const handleStyleValueChange = useCallback(
    (stylePath: string, newValue: unknown) => {
      if (onStyleChange) {
        onStyleChange(stylePath, newValue);
      }
    },
    [onStyleChange]
  );

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        mb: FIELD_MARGIN_BOTTOM,
      }}
    >
      {/* Label Row: Checkbox + Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL }}>
        <Checkbox
          checked={enabled}
          onChange={handleCheckboxChange}
          disabled={disabled}
          size="small"
          icon={<Box sx={checkboxIconSx} />}
          checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          sx={checkboxSx}
          data-testid={checkboxTestId}
          inputProps={{
            'aria-label': fieldConfig.label,
            'data-testid': checkboxTestId ? `${checkboxTestId}-input` : undefined,
          } as React.InputHTMLAttributes<HTMLInputElement>}
        />
        {/* Label with optional guide help */}
        {fieldConfig.guideKey && guideService ? (
          <WithGuideHelp
            guideKey={fieldConfig.guideKey}
            guideService={guideService}
            data-testid={`guide-anchor-${fieldConfig.guideKey}`}
            position="inline-end"
            iconSize="small"
          >
            <Tooltip
              title={SHOW_FIELD_PATH_TOOLTIP ? fieldConfig.path : ''}
              placement="top"
              arrow
              disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
            >
              <Typography
                variant="body2"
                sx={enabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
                data-testid={fieldConfig.path}
                aria-label={fieldConfig.path}
              >
                {fieldConfig.label}
              </Typography>
            </Tooltip>
          </WithGuideHelp>
        ) : (
          <Tooltip
            title={SHOW_FIELD_PATH_TOOLTIP ? fieldConfig.path : ''}
            placement="top"
            arrow
            disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
          >
            <Typography
              variant="body2"
              sx={enabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
              data-testid={fieldConfig.path}
              aria-label={fieldConfig.path}
            >
              {fieldConfig.label}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Input Row: TextField + Gear */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
        <Box sx={{ flex: 1 }}>
          {enablePaywallVariables ? (
            <PaywallVariableTextField
              value={enabled ? value : ''}
              onChange={handleInputChange}
              disabled={disabled || !enabled}
              size="small"
              fullWidth
              placeholder={fieldConfig.placeholder || fieldConfig.label}
              aria-label={fieldConfig.label}
              sx={mainFieldInputSx}
              inputProps={{
                'data-testid': inputTestId,
              }}
            />
          ) : (
            <StyledTextField
              value={enabled ? value : ''}
              onChange={handleInputChange}
              disabled={disabled || !enabled}
              size="small"
              fullWidth
              placeholder={fieldConfig.placeholder || fieldConfig.label}
              aria-label={fieldConfig.label}
              sx={mainFieldInputSx}
              inputProps={{
                'data-testid': inputTestId,
              }}
            />
          )}
        </Box>

        {/* Gear Icon (only if any styleFields exist) */}
        {hasAnyStyleFields && (
          <IconButton
            data-testid={stylesToggleTestId}
            onClick={handleGearClick}
            size="small"
            disabled={disabled}
            aria-label="Toggle style settings"
            sx={isStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Style Settings Panel (Expandable) */}
      {hasAnyStyleFields && (
        <Collapse in={isStylesExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: FIELD_STYLE_MARGIN_TOP, ...expandedSectionSx }}>
            {/* V2 Accordion Groups (when buttonStyleGroups present) */}
            {hasButtonStyleGroups && (
              <ButtonStyleSections
                sections={fieldConfig.buttonStyleGroups!}
                values={buttonStyleValues}
                onChange={handleStyleValueChange}
                disabled={disabled || !enabled}
                locale={locale}
                onIconUpload={uploadService ? handleIconUpload : undefined}
              />
            )}

            {/* V1 Flat Grid (when only buttonStyleFields, no groups) */}
            {!hasButtonStyleGroups && (hasButtonStyleFields || hasStyleFields) && (
              <Box
                data-testid={styleGroupTestId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                }}
              >
                {/* Button Container Style Fields (rendered first) */}
                {hasButtonStyleFields &&
                  fieldConfig.buttonStyleFields!.map((styleConfig) => (
                    <StyleFieldRow
                      key={styleConfig.path}
                      config={styleConfig}
                      value={buttonStyleValues[styleConfig.path]}
                      onChange={(newValue) =>
                        handleStyleValueChange(styleConfig.path, newValue)
                      }
                      disabled={disabled || !enabled}
                      locale={locale}
                      inputTestId={getStyleInputTestId(fieldConfig.label, styleConfig.label)}
                      checkboxTestId={getStyleCheckboxTestId(fieldConfig.label, styleConfig.label)}
                    />
                  ))}
                {/* Text Content Style Fields (rendered after button styles) */}
                {hasStyleFields &&
                  fieldConfig.styleFields!.map((styleConfig) => (
                    <StyleFieldRow
                      key={styleConfig.path}
                      config={styleConfig}
                      value={styleValues[styleConfig.path]}
                      onChange={(newValue) =>
                        handleStyleValueChange(styleConfig.path, newValue)
                      }
                      disabled={disabled || !enabled}
                      inputTestId={getStyleInputTestId(fieldConfig.label, styleConfig.label)}
                      checkboxTestId={getStyleCheckboxTestId(fieldConfig.label, styleConfig.label)}
                    />
                  ))}
              </Box>
            )}

            {/* Text style fields below groups (V2 mode: text styles are NOT inside groups) */}
            {hasButtonStyleGroups && hasStyleFields && (
              <Box
                data-testid={styleGroupTestId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                  mt: 1,
                }}
              >
                {fieldConfig.styleFields!.map((styleConfig) => (
                  <StyleFieldRow
                    key={styleConfig.path}
                    config={styleConfig}
                    value={styleValues[styleConfig.path]}
                    onChange={(newValue) =>
                      handleStyleValueChange(styleConfig.path, newValue)
                    }
                    disabled={disabled || !enabled}
                    inputTestId={getStyleInputTestId(fieldConfig.label, styleConfig.label)}
                    checkboxTestId={getStyleCheckboxTestId(fieldConfig.label, styleConfig.label)}
                  />
                ))}
              </Box>
            )}

            {/* URI Field - rendered for NavLink fields (Terms of Use, Privacy Policy) */}
            {hasUriField && uriValue !== undefined && onUriChange && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  URL
                </Typography>
                <StyledTextField
                  value={uriValue ?? ''}
                  onChange={(val: string) => onUriChange(val)}
                  placeholder="https://"
                  disabled={disabled || !enabled}
                  size="small"
                  fullWidth
                  sx={mainFieldInputSx}
                />
              </Box>
            )}
            {/* Padding Element (cross-layout) - rendered when paddingConfig exists */}
            {hasPaddingConfig && (() => {
              const basePath = fieldConfig.paddingConfig!.basePath;
              const paddingValues: PaddingValues = {
                paddingTop: (styleValues[`${basePath}.paddingTop`] as number) ?? 0,
                paddingRight: (styleValues[`${basePath}.paddingRight`] as number) ?? 0,
                paddingBottom: (styleValues[`${basePath}.paddingBottom`] as number) ?? 0,
                paddingLeft: (styleValues[`${basePath}.paddingLeft`] as number) ?? 0,
              };
              return (
                <PaddingElement
                  basePath={basePath}
                  values={paddingValues}
                  onChange={handleStyleValueChange}
                  label={fieldConfig.paddingConfig!.label}
                  disabled={disabled || !enabled}
                />
              );
            })()}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default TextElement;
